import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Autocomplete,
    IconButton,
    Divider,
    Alert,
    CircularProgress,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";

export default function DiagnosticoPDF() {
    const [personal, setPersonal] = useState([]);        // todos los personales
    const [personalTI, setPersonalTI] = useState([]);    // solo del dep. TI
    const [selected, setSelected] = useState(null);      // persona en Atención
    const [firmas, setFirmas] = useState({ firma1: null, firma2: null }); // firmantes seleccionados

    const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
    const [asunto, setAsunto] = useState("");
    const [ubicacion, setUbicacion] = useState("Hotel Malibu");
    const [telefono, setTelefono] = useState("3338805660");
    const [items, setItems] = useState([
        { diagnosis: "", correction: "", image: null, imagePreview: null },
    ]);
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(true);

    // Cargar personal y TI al inicio
  useEffect(() => {
    async function loadData() {
        try {
            setLoadingData(true);

            // Traer todos los personales
            const resPers = await fetch("https://api.hmsaa.com.mx:5001/api/personales");
            const dataPers = await resPers.json();

            const personales = Array.isArray(dataPers)
            ? dataPers
            : dataPers.personales || [];

            setPersonal(personales);

            // Filtrar los del departamento TI
            const ti = personales.filter(p =>
            (p.departamento || "").toLowerCase().includes("ti")
            );
            setPersonalTI(ti);

            // Normalizar datos para Autocomplete
            const personalesFormateados = (Array.isArray(dataPers) ? dataPers : dataPers.personales || []).map(p => ({
                id_personal: p.id || p.id_personal, // asegurar id
                nombre: p.nombre,
                departamento: p.departamento
            }));
            setPersonal(personalesFormateados);

        } catch (err) {
            console.error("Error cargando personal:", err);
        } finally {
            setLoadingData(false);
        }
    }

    loadData();
}, []);


    // Añadir diagnóstico
    const addItem = () => {
        setItems((prev) => [
            ...prev,
            { diagnosis: "", correction: "", image: null, imagePreview: null },
        ]);
    };

    // Eliminar diagnóstico
    const removeItem = (index) => {
        setItems((prev) => prev.filter((_, i) => i !== index));
    };

    // Cambiar texto
    const updateItem = (index, field, value) => {
        setItems((prev) =>
            prev.map((it, i) => (i === index ? { ...it, [field]: value } : it))
        );
    };

    // Imagen
    const handleImage = (index, file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result;
            updateItem(index, "image", base64);
            updateItem(index, "imagePreview", base64);
        };
        reader.readAsDataURL(file);
    };

    // Generar PDF
    const handleGeneratePDF = async () => {
        if (!selected) {
            alert("Selecciona una persona en el campo 'Atención'.");
            return;
        }

        if (!firmas.firma1 || !firmas.firma2) {
            alert("Selecciona los dos firmantes del departamento TI.");
            return;
        }

        const payload = {
            meta: {
                fecha,
                atencion: selected.nombre,
                asunto,
                ubicacion,
                telefono,
                firma1: firmas.firma1.nombre,
                firma2: firmas.firma2.nombre,
            },
            items: items.map((it) => ({
                diagnosis: it.diagnosis,
                correction: it.correction,
                image: it.image,
            })),
        };

        setLoading(true);
        try {
            const res = await fetch("https://api.hmsaa.com.mx:5001/api/generate-pdf", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `diagnostico_${selected.nombre}.pdf`;
            a.click();
            a.remove();
        } catch (err) {
            console.error(err);
            alert("Error al generar el PDF");
        } finally {
            setLoading(false);
        }
    };

    if (loadingData) {
        return (
            <Box sx={{ mt: 8, textAlign: "center" }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ mt: 2 }}>
                    Cargando datos del personal...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 900, mx: "auto", mt: 4, p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Diagnóstico de Equipos de TI
            </Typography>

            {/* Datos Generales */}
            <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "1fr 1fr" }}>
                <TextField
                    label="Fecha"
                    type="date"
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                    fullWidth
                />

                {/* Buscador de atención */}
                <Autocomplete
                    options={personal}
                    loading={loadingData}
                    getOptionLabel={(opt) => opt.nombre || ""}
                    value={selected}
                    onChange={(_, val) => setSelected(val)}
                    isOptionEqualToValue={(opt, val) => opt.id_personal === val?.id_personal}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Atención (buscar por nombre)"
                            placeholder="Escribe para buscar..."
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loadingData ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
            </Box>

            <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "1fr 1fr", mt: 2 }}>
                <TextField
                    label="Asunto"
                    value={asunto}
                    onChange={(e) => setAsunto(e.target.value)}
                    fullWidth
                />
                <TextField
                    label="Ubicación"
                    value={ubicacion}
                    onChange={(e) => setUbicacion(e.target.value)}
                    fullWidth
                />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Diagnósticos */}
            <Typography variant="h6" gutterBottom>
                Diagnósticos y correcciones
            </Typography>

            {items.map((it, idx) => (
                <Box
                    key={idx}
                    sx={{
                        border: "1px solid #ccc",
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        background: "#fafafa",
                    }}
                >
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="subtitle1">Diagnóstico #{idx + 1}</Typography>
                        <IconButton color="error" onClick={() => removeItem(idx)}>
                            <DeleteIcon />
                        </IconButton>
                    </Box>

                    <TextField
                        label="Descripción del diagnóstico"
                        multiline
                        rows={2}
                        fullWidth
                        sx={{ mt: 1 }}
                        value={it.diagnosis}
                        onChange={(e) => updateItem(idx, "diagnosis", e.target.value)}
                    />

                    <TextField
                        label="Corrección o acción aplicada"
                        multiline
                        rows={2}
                        fullWidth
                        sx={{ mt: 2 }}
                        value={it.correction}
                        onChange={(e) => updateItem(idx, "correction", e.target.value)}
                    />

                    <Box sx={{ mt: 2 }}>
                        <Button variant="outlined" component="label">
                            📷 Subir evidencia
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={(e) => handleImage(idx, e.target.files[0])}
                            />
                        </Button>
                    </Box>

                    {it.imagePreview && (
                        <img
                            src={it.imagePreview}
                            alt="preview"
                            style={{ maxWidth: "150px", marginTop: "10px", borderRadius: 8 }}
                        />
                    )}
                </Box>
            ))}

            <Button
                startIcon={<AddCircleIcon />}
                variant="contained"
                sx={{ mb: 3 }}
                onClick={addItem}
            >
                Añadir diagnóstico
            </Button>

            <Divider sx={{ my: 3 }} />

            {/* Firmas seleccionables */}
            <Typography variant="h6" gutterBottom>
                Selecciona los firmantes del departamento TI
            </Typography>

            {personalTI.length > 0 ? (
                <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "1fr 1fr", mb: 3 }}>
                    <Autocomplete
                        options={personalTI}
                        getOptionLabel={(opt) => opt.nombre || ""}
                        value={firmas.firma1}
                        onChange={(_, val) => setFirmas((f) => ({ ...f, firma1: val }))}
                        isOptionEqualToValue={(opt, val) => opt.id_personal === val?.id_personal}
                        renderInput={(params) => <TextField {...params} label="Firma 1 (Auxiliar / Técnico)" />}
                    />
                    <Autocomplete
                        options={personalTI}
                        getOptionLabel={(opt) => opt.nombre || ""}
                        value={firmas.firma2}
                        onChange={(_, val) => setFirmas((f) => ({ ...f, firma2: val }))}
                        isOptionEqualToValue={(opt, val) => opt.id_personal === val?.id_personal}
                        renderInput={(params) => <TextField {...params} label="Firma 2 (Gerente / Supervisor)" />}
                    />
                </Box>
            ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    No hay personal registrado en el departamento TI.
                </Alert>
            )}

            <Button
                variant="contained"
                color="primary"
                onClick={handleGeneratePDF}
                disabled={loading}
                size="large"
            >
                {loading ? "Generando PDF..." : "📄 Generar PDF"}
            </Button>
        </Box>
    );
}
